// client/src/coms/settings/sections/MobileLayoutSettings.js
// Personal Settings → Mobile Layout (phones only; desktops see Layout & Interface instead). Changes are made to a
// draft shown in a live preview built from the real header and navigation components, then applied, cancelled or
// reset. Saved to the account, separate from the desktop layout.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Row, Section, Segmented, Slider, Switch } from '../../personalisation/settingsControls';
import { usePersonalisation } from '../../personalisation/PersonalisationProvider';
import { DEFAULT_PREFS } from '../../personalisation/prefsSchema';
import {
  MOBILE_BAR_MAX,
  MOBILE_NAV_BY_ID,
  MOBILE_NAV_LOCKED,
  MOBILE_NAV_MAX,
  MOBILE_NAV_OPTIONS,
  MOBILE_PRESET_INFO,
  MOBILE_PRESET_STRUCTURE,
  applyMobilePreset,
  barItems,
  defaultMobile,
  layoutParts,
  layoutValuesOf,
  moveNavItem,
  setMobileValue,
  toggleNavItem,
  withMobileValues,
} from '../../personalisation/mobileLayout';
import MobileHeader from '../../components/mobile/MobileHeader';
import MobileNavBar, { NavGlyph } from '../../components/mobile/MobileNavBar';
import { MobileMenuLauncher } from '../../components/mobile/MobileNavMenu';
import NavIcon from '../../components/mobile/NavIcon';
import { GESTURE_ACTIONS, GESTURE_DIRECTIONS, assignGesture } from '../../components/mobile/gestureRules';
import './mobileLayoutEditor.css';

const D = DEFAULT_PREFS.mobile;
const SAVED_MAX = 5;

const POSITIONS = [
  { value: 'bottom', label: 'Bottom bar' },
  { value: 'top', label: 'Top tabs' },
  { value: 'dropdown-top', label: 'Menu from the top' },
  { value: 'dropdown-bottom', label: 'Menu from the bottom' },
  { value: 'gesture', label: 'Gestures' },
];
const STYLES = [
  { value: 'icons-labels', label: 'Icons + words' },
  { value: 'icons', label: 'Icons' },
  { value: 'labels', label: 'Words' },
  { value: 'emoji', label: 'Emoji' },
  { value: 'emoji-labels', label: 'Emoji + words' },
];
const BEHAVIOURS = [
  { value: 'always', label: 'Always visible' },
  { value: 'autohide', label: 'Hide while scrolling' },
  { value: 'compact', label: 'Shrink when idle' },
];

// Text size and the swipe switch are not part of a layout but are edited here too (and so also wait for Apply).
const editableOf = (m) => ({ ...layoutValuesOf(m), messageScale: m.messageScale, swipe: m.swipe });
const sameEditable = (a, b) => JSON.stringify(editableOf(a)) === JSON.stringify(editableOf(b));

// ---------- live preview: the real header and navigation, with a sample conversation ----------
const SAMPLE = [
  { who: 'Mira', c: '#c084fc', text: 'Who is around for the session on Friday?' },
  { who: 'Tobias', c: '#38bdf8', text: 'Me! I will bring the new map.' },
  { who: 'Mira', c: '#c084fc', text: 'Great. Voice at 8?' },
  { who: 'Ren', c: '#4ade80', text: 'Count me in 🎲' },
  { who: 'Tobias', c: '#38bdf8', text: 'See you all there.' },
];

export function MobileLayoutPreview({ mobile, inVoice = true }) {
  const parts = layoutParts(mobile);
  const compactChat = mobile.chatDensity === 'compact';
  const showTabs = mobile.tabBar === 'always' || mobile.tabBar === 'auto';
  const noop = () => {};
  const bar = (position) => <MobileNavBar nav={mobile.nav} position={position} style={mobile.navStyle} size={mobile.navSize} overflow={mobile.navOverflow} behaviour="always" activeTab="coms" onNavigate={noop} badges={{ chats: 2 }} publish={false} />;
  return (
    <div className="vx-mlp" role="img" aria-label="Preview of this mobile layout" data-testid="mobile-preview">
      <div className={`vx-mlp-screen${mobile.density === 'compact' ? ' is-compact' : ''}`} inert="">
        <MobileHeader
          left={{ kind: 'server', label: 'Guild of Maps', name: 'Guild of Maps' }}
          title="general"
          titlePrefix="#"
          subtitle="Guild of Maps"
          size={mobile.headerSize}
          menu={parts.menuFromHeader || parts.handle ? { open: false, onToggle: noop, label: 'Open navigation' } : null}
          actions={[
            { id: 'p-switch', icon: <NavIcon id="switch" />, label: 'Switch conversation', onClick: noop },
            { id: 'p-members', icon: <NavIcon id="members" />, label: 'Members', onClick: noop },
          ]}
        >
          {parts.topBar ? bar('top') : null}
        </MobileHeader>
        {showTabs ? (
          <div className="vx-mlp-tabs" aria-hidden="true">
            <span className="is-on"># general</span>
            <span>@ Tobias</span>
          </div>
        ) : null}
        {mobile.headerBar ? <div className="vx-mlp-banner">📌 Session notes are pinned</div> : null}
        <div className={`vx-mlp-chat${compactChat ? ' is-compact' : ''}`} style={{ fontSize: `${(13 * mobile.messageScale).toFixed(1)}px` }}>
          {SAMPLE.map((m, i) => (
            <div key={i} className="vx-mlp-msg">
              {compactChat ? null : <i style={{ background: m.c }} />}
              <div>
                <b style={{ color: m.c }}>{m.who}</b>
                {compactChat ? ' ' : <br />}
                <span>{m.text}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="vx-mlp-composer">
          <span>Message #general</span>
          <b>Send</b>
        </div>
        {inVoice && mobile.voiceDock === 'always' ? (
          <div className="vx-mlp-dock">
            <span>● 🔊 Lounge</span>
            <span>🎙️ 🎧 ⏻</span>
          </div>
        ) : null}
        {parts.bottomBar ? bar('bottom') : null}
        {parts.bottomLauncher || parts.handle ? <MobileMenuLauncher kind={parts.handle ? 'handle' : 'launcher'} open={false} label="Menu" onToggle={noop} publish={false} /> : null}
      </div>
    </div>
  );
}

// A tiny drawing of where navigation sits, for the preset cards.
function PresetSketch({ id }) {
  const s = MOBILE_PRESET_STRUCTURE[id];
  const p = s ? s.navPosition : 'bottom';
  return (
    <span className={`vx-psk is-${p}${s && s.headerSize === 'slim' ? ' is-slim' : ''}`} aria-hidden="true">
      <span className="vx-psk-head" />
      {p === 'top' ? <span className="vx-psk-tabs" /> : null}
      {p === 'dropdown-top' ? <span className="vx-psk-drop" /> : null}
      <span className="vx-psk-body" />
      {p === 'bottom' ? <span className={`vx-psk-bar${s && s.navSize === 'large' ? ' is-large' : s && s.navSize === 'compact' ? ' is-small' : ''}`} /> : null}
      {p === 'dropdown-bottom' ? <span className="vx-psk-launch" /> : null}
      {p === 'gesture' ? <span className="vx-psk-handle" /> : null}
    </span>
  );
}

function NavEditor({ nav, overflow, position, onChange }) {
  const available = MOBILE_NAV_OPTIONS.filter((o) => !nav.includes(o.id));
  const isBar = position === 'bottom' || position === 'top';
  const inBar = isBar ? barItems(nav, overflow) : nav;
  return (
    <div className="vx-navedit" data-testid="mobile-nav-editor">
      <ol className="vx-navedit-list" aria-label="Your destinations, in order">
        {nav.map((id, i) => {
          const o = MOBILE_NAV_BY_ID[id];
          const locked = MOBILE_NAV_LOCKED.includes(id);
          const hiddenInBar = isBar && !inBar.includes(id);
          return (
            <li key={id} className={`vx-navedit-item${hiddenInBar ? ' is-overflow' : ''}`} data-testid={`mobile-nav-item-${id}`}>
              <span className="vx-navedit-icon" aria-hidden="true">
                <NavIcon id={id} size={20} />
              </span>
              <span className="vx-navedit-label">
                {o.label}
                {locked ? <span className="vx-set-hint"> · always there</span> : hiddenInBar ? <span className="vx-set-hint"> · under More</span> : null}
              </span>
              <button type="button" className="vx-icon-btn" disabled={i === 0} onClick={() => onChange(moveNavItem(nav, id, -1))} aria-label={`Move ${o.label} earlier`} data-testid={`mobile-nav-up-${id}`}>
                ↑
              </button>
              <button type="button" className="vx-icon-btn" disabled={i === nav.length - 1} onClick={() => onChange(moveNavItem(nav, id, 1))} aria-label={`Move ${o.label} later`} data-testid={`mobile-nav-down-${id}`}>
                ↓
              </button>
              <button type="button" className="vx-icon-btn" disabled={locked || nav.length <= 2} onClick={() => onChange(toggleNavItem(nav, id))} aria-label={`Remove ${o.label}`} data-testid={`mobile-nav-remove-${id}`}>
                ✕
              </button>
            </li>
          );
        })}
      </ol>
      <div className="vx-navedit-add">
        <span className="vx-set-hint">{nav.length >= MOBILE_NAV_MAX ? `That is the most (${MOBILE_NAV_MAX}). Remove one to add another; everything else stays under More.` : 'Add:'}</span>
        <div className="vx-inline vx-navedit-chips">
          {available.map((o) => (
            <button key={o.id} type="button" className="vx-btn small" disabled={nav.length >= MOBILE_NAV_MAX} onClick={() => onChange(toggleNavItem(nav, o.id))} title={o.hint} data-testid={`mobile-nav-add-${o.id}`}>
              <NavGlyph id={o.id} variant="icons-labels" size={16} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function SavedLayouts({ saved, draft, onUse, onChange }) {
  const [name, setName] = useState('');
  const clean = name.replace(/\s+/g, ' ').trim().slice(0, 30);
  const replacing = saved.some((s) => s.name === clean);
  const full = saved.length >= SAVED_MAX && !replacing;
  const save = () => {
    if (!clean || full) return;
    onChange([...saved.filter((s) => s.name !== clean), { name: clean, layout: layoutValuesOf(draft) }]);
    setName('');
  };
  return (
    <div className="vx-mle-saved" data-testid="mobile-saved">
      {saved.length ? (
        <ul className="vx-mle-saved-list">
          {saved.map((s) => (
            <li key={s.name}>
              <span className="vx-mle-saved-name">{s.name}</span>
              <button type="button" className="vx-btn small" onClick={() => onUse(s)} data-testid={`mobile-saved-use-${s.name}`}>
                Use
              </button>
              <button type="button" className="vx-icon-btn" onClick={() => onChange(saved.filter((x) => x.name !== s.name))} aria-label={`Delete ${s.name}`} data-testid={`mobile-saved-delete-${s.name}`}>
                ✕
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="vx-set-hint">No saved layouts yet. Save this one to come back to it in one tap.</p>
      )}
      <div className="vx-inline">
        <input
          type="text"
          value={name}
          maxLength={30}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && save()}
          placeholder="Name, e.g. One-handed"
          aria-label="Name for this layout"
          data-testid="mobile-saved-name"
        />
        <button type="button" className="vx-btn small" disabled={!clean || full} onClick={save} data-testid="mobile-saved-save">
          {replacing ? 'Replace' : 'Save layout'}
        </button>
      </div>
      {full ? <span className="vx-set-hint">You can keep {SAVED_MAX}. Delete one to save another.</span> : null}
    </div>
  );
}

export default function MobileLayoutSettings() {
  const pz = usePersonalisation();
  const current = pz.mobilePrefs;
  const [draft, setDraft] = useState(current);
  const [previewOpen, setPreviewOpen] = useState(true);
  const dirty = !sameEditable(draft, current);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  // Changes saved elsewhere (another device, the More sheet) show up here unless you are in the middle of editing.
  useEffect(() => {
    if (!dirtyRef.current) setDraft(current);
  }, [current]);

  const edit = (key, value) => setDraft((d) => setMobileValue(d, key, value));
  const parts = layoutParts(draft);
  const isBar = parts.bottomBar || parts.topBar;
  const presetLabel = useMemo(() => (MOBILE_PRESET_INFO.find((p) => p.id === draft.preset) || {}).label || 'Custom', [draft.preset]);
  const isDefault = sameEditable(draft, defaultMobile());

  const apply = () => {
    const values = {};
    for (const k of Object.keys(editableOf(draft))) values[k] = draft[k];
    pz.setMobileValues(values);
  };

  return (
    <Section
      id="vx-cat-mobile"
      title="Mobile Layout"
      description="How Voxemera looks and moves on this phone. Separate from your desktop layout: changing one never changes the other. Try changes in the preview, then Apply."
    >
      {/* Stays in view while you scroll the options: the preview beside Apply, Cancel and Reset. */}
      <div className={`vx-mle-top${previewOpen ? '' : ' is-closed'}${dirty ? ' is-dirty' : ''}`} data-testid="mobile-layout-top">
        <button type="button" className="vx-mle-preview-toggle" onClick={() => setPreviewOpen((v) => !v)} aria-expanded={previewOpen} data-testid="mobile-preview-toggle">
          <span>
            {previewOpen ? 'Hide preview' : 'Show preview'} · <b>{presetLabel}</b>
          </span>
          <NavIcon id={previewOpen ? 'up' : 'down'} size={16} />
        </button>
        <div className="vx-mle-top-body">
          {previewOpen ? <MobileLayoutPreview mobile={draft} /> : null}
          <div className="vx-mle-actions" role="toolbar" aria-label="Mobile layout changes" data-testid="mobile-actions">
            <span className="vx-mle-status" role="status">
              {dirty ? 'Not applied yet' : 'In use'}
            </span>
            <button type="button" className="vx-btn small primary" disabled={!dirty} onClick={apply} data-testid="mobile-apply">
              Apply
            </button>
            <button type="button" className="vx-btn small" disabled={!dirty} onClick={() => setDraft(current)} data-testid="mobile-cancel">
              Cancel
            </button>
            <button type="button" className="vx-btn small" disabled={isDefault} onClick={() => setDraft((d) => ({ ...defaultMobile(), saved: d.saved }))} title="Back to the Classic defaults (then Apply)" data-testid="mobile-reset">
              Reset
            </button>
          </div>
        </div>
      </div>

      <h4 className="vx-set-subtitle" id="vx-setting-mobile-preset" data-setting="mobile-preset">
        Start from a layout
      </h4>
      <div className="vx-mle-presets" role="radiogroup" aria-label="Mobile layout" data-testid="mobile-presets">
        {MOBILE_PRESET_INFO.map((p) => {
          const custom = p.id === 'custom';
          return (
            <button
              key={p.id}
              type="button"
              role="radio"
              aria-checked={draft.preset === p.id}
              className={`vx-mle-preset${draft.preset === p.id ? ' is-on' : ''}`}
              onClick={() => (custom ? null : setDraft((d) => applyMobilePreset(d, p.id)))}
              disabled={custom && draft.preset !== 'custom'}
              title={custom && draft.preset !== 'custom' ? 'Change any option below to make your own layout' : undefined}
              data-testid={`mobile-preset-${p.id}`}
            >
              <PresetSketch id={p.id} />
              <span className="vx-mle-preset-text">
                <span className="vx-mle-preset-title">{p.label}</span>
                <span className="vx-mle-preset-sub">{p.description}</span>
              </span>
            </button>
          );
        })}
      </div>

      <h4 className="vx-set-subtitle" id="vx-setting-mobile-nav" data-setting="mobile-nav">
        Navigation
      </h4>
      <Row label="Where it lives" scope="account">
        <Segmented label="Navigation position" value={draft.navPosition} options={POSITIONS} onChange={(v) => edit('navPosition', v)} testId="mobile-nav-position" />
      </Row>
      <Row label="Looks like" scope="account">
        <Segmented label="Navigation appearance" value={draft.navStyle} options={STYLES} onChange={(v) => edit('navStyle', v)} testId="mobile-nav-style" />
      </Row>
      {isBar ? (
        <>
          <Row label="Behaviour" hint={parts.topBar ? 'Hiding while scrolling gives the conversation the tab row’s space while you read.' : 'Hide while scrolling gives the conversation the bar’s space while you read back; it returns when you scroll down.'} scope="account">
            <Segmented label="Navigation behaviour" value={draft.navBehaviour} options={BEHAVIOURS} onChange={(v) => edit('navBehaviour', v)} testId="mobile-nav-behaviour" />
          </Row>
          <Row label="Size" scope="account">
            <Segmented
              label="Navigation size"
              value={draft.navSize}
              options={[
                { value: 'compact', label: 'Small' },
                { value: 'regular', label: 'Regular' },
                { value: 'large', label: 'Large' },
              ]}
              onChange={(v) => edit('navSize', v)}
              testId="mobile-nav-size"
            />
          </Row>
          <Row label="When there are more than fit" hint={`A bar with More shows ${MOBILE_BAR_MAX - 1} destinations and puts the rest under More; a scrolling bar shows them all.`} scope="account">
            <Segmented
              label="Navigation overflow"
              value={draft.navOverflow}
              options={[
                { value: 'more', label: 'More button' },
                { value: 'scroll', label: 'Scroll sideways' },
              ]}
              onChange={(v) => edit('navOverflow', v)}
              testId="mobile-nav-overflow"
            />
          </Row>
        </>
      ) : (
        <p className="vx-set-hint vx-mle-note">
          {parts.handle
            ? 'Swipe up from the handle (or tap it, or tap the conversation name) to open navigation.'
            : parts.menuFromHeader
              ? 'Tap the conversation name at the top to open navigation.'
              : 'Tap Menu at the bottom to open navigation.'}{' '}
          The menu lists your destinations first and everything else after them.
        </p>
      )}
      <Row label="Header" hint="The top bar with the conversation's name." scope="account">
        <Segmented
          label="Header size"
          value={draft.headerSize}
          options={[
            { value: 'slim', label: 'Slim' },
            { value: 'regular', label: 'Regular' },
            { value: 'large', label: 'Large' },
          ]}
          onChange={(v) => edit('headerSize', v)}
          testId="mobile-header-size"
        />
      </Row>

      <h4 className="vx-set-subtitle">Destinations</h4>
      <p className="vx-set-hint">In your order. More always stays: it holds everything else, including Settings, so you can never lose your way.</p>
      <NavEditor nav={draft.nav} overflow={draft.navOverflow} position={draft.navPosition} onChange={(nav) => edit('nav', nav)} />

      <h4 className="vx-set-subtitle">Screen</h4>
      <Row label="Voice controls while in a call" hint="A slim bar with mute, deafen and leave on every screen, or only in the channel list." settingId="mobile-voice" scope="account">
        <Segmented
          label="Voice controls"
          value={draft.voiceDock}
          options={[
            { value: 'always', label: 'Always visible' },
            { value: 'drawer', label: 'Channel list only' },
          ]}
          onChange={(v) => edit('voiceDock', v)}
          testId="mobile-voice-dock"
        />
      </Row>
      <Row label="Conversation tabs" hint="Small chips for the conversations you have open." scope="account">
        <Segmented
          label="Conversation tabs"
          value={draft.tabBar}
          options={[
            { value: 'auto', label: 'Automatic' },
            { value: 'always', label: 'Always' },
            { value: 'never', label: 'Never' },
          ]}
          onChange={(v) => edit('tabBar', v)}
          testId="mobile-tabbar"
        />
      </Row>
      <Row label="Pinned message banner" scope="account">
        <Switch label="Pinned message banner" checked={draft.headerBar} onChange={(v) => edit('headerBar', v)} testId="mobile-headerbar" />
      </Row>
      <Row label="Spacing" settingId="mobile-density" scope="account">
        <Segmented
          label="Mobile spacing"
          value={draft.density}
          options={[
            { value: 'comfortable', label: 'Comfortable' },
            { value: 'compact', label: 'Compact' },
          ]}
          onChange={(v) => edit('density', v)}
          testId="mobile-density"
        />
      </Row>
      <Row label="Chat density" scope="account">
        <Segmented
          label="Mobile chat density"
          value={draft.chatDensity}
          options={[
            { value: 'cozy', label: 'Cozy' },
            { value: 'compact', label: 'Compact' },
          ]}
          onChange={(v) => edit('chatDensity', v)}
          testId="mobile-chat-density"
        />
      </Row>
      <Row label="Message text size on phones" settingId="mobile-text" scope="account" onReset={() => edit('messageScale', D.messageScale)} isDefault={draft.messageScale === D.messageScale}>
        <Slider label="Message text size on phones" value={draft.messageScale} min={0.8} max={1.5} step={0.05} onChange={(v) => edit('messageScale', v)} format={(v) => `${Math.round(v * 100)}%`} testId="mobile-message-scale" />
      </Row>

      <h4 className="vx-set-subtitle" id="vx-setting-mobile-gestures" data-setting="mobile-gestures">
        Gestures
      </h4>
      <Row label="Swipe gestures" hint="Never in Canvas, Activities, Studio or Maps, on text you are selecting, on sideways-scrolling rows or at the screen edges (Android's Back)." settingId="mobile-swipe" scope="account">
        <Switch label="Swipe gestures" checked={draft.swipe} onChange={(v) => edit('swipe', v)} testId="mobile-swipe" />
      </Row>
      {draft.swipe ? (
        <div className="vx-mle-gestures" data-testid="mobile-gestures">
          {GESTURE_DIRECTIONS.map((g) => {
            const value = draft.gestures[g.id];
            const action = GESTURE_ACTIONS.find((a) => a.id === value);
            const unavailable = g.id === 'swipeUp' && parts.menuFromHeader;
            return (
              <label key={g.id} className="vx-mle-gesture">
                <span className="vx-mle-gesture-text">
                  <span className="vx-mle-gesture-label">{g.label}</span>
                  <span className="vx-set-hint">
                    {unavailable ? 'This layout has nothing at the bottom to swipe from.' : g.hint}
                    {action && action.alt && !unavailable ? ` Also: ${action.alt}.` : ''}
                  </span>
                </span>
                <select value={value} onChange={(e) => edit('gestures', assignGesture(draft.gestures, g.id, e.target.value))} data-testid={`mobile-gesture-${g.id}`} aria-label={g.label}>
                  {GESTURE_ACTIONS.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.label}
                    </option>
                  ))}
                </select>
              </label>
            );
          })}
          <p className="vx-set-hint">Each action belongs to one swipe: picking one that another swipe uses moves it here.</p>
        </div>
      ) : null}

      <h4 className="vx-set-subtitle" id="vx-setting-mobile-saved" data-setting="mobile-saved">
        Your saved layouts
      </h4>
      <SavedLayouts saved={current.saved || []} draft={draft} onUse={(s) => setDraft((d) => withMobileValues(d, s.layout))} onChange={(list) => pz.setMobileValue('saved', list)} />

      <p className="vx-set-hint vx-mle-note" id="vx-setting-mobile-recovery" data-setting="mobile-recovery">
        If a layout ever gets in your way, More (or the menu) always has Settings, and opening Voxemera with <code>?resetMobileLayout=1</code> at the end of the address restores the mobile defaults.
      </p>
    </Section>
  );
}

